import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ComoFunciona, { ComoFuncionaSecao } from '../../../../components/como-funciona/ComoFunciona';
import '../../../../components/como-funciona/help-circle-btn.css';
import {
  buscarRecomendacoes,
  RadarCard as CardDoRadar,
} from '../../../../api/contac/contacRecomendacoesApi';
import { AchadoDoAssistente } from '../../../../api/radarApi';
import EsqueletoDeCard from '../../../../components/radar/EsqueletoDeCard';
import LinhasDoAchadoModal from '../../../../components/radar/LinhasDoAchadoModal';
import RadarCard from '../../../../components/radar/RadarCard';
import SecaoAchadosDoAssistente from '../../../../components/radar/SecaoAchadosDoAssistente';
import { secoesDaProveniencia } from '../../../../components/radar/secoesDaProveniencia';
import { notify } from '../../../../shared/feedback/notify';
import '../../../../shared/feedback/erro-de-tela.css';
import '../../../recepcao/pages/Recepcao.css';
import '../../../../shared/ui/dashboard-base.css';
import DetalheRecomendacaoModal from '../components/DetalheRecomendacaoModal';
// O visual do Radar virou CSS de COMPONENTE na at-82: as duas telas importam daqui.
import '../../../../components/radar/radar.css';
import '../../../../shared/feedback/aviso-de-tela.css';

/**
 * Radar Financeiro (contac/at-11) — o que merece atenção no Contac, sem ninguém precisar procurar.
 *
 * ## A regra que governa esta tela
 *
 * "Nenhuma recomendação" é um estado LEGÍTIMO e desejável aqui — significa que nada cruzou os
 * limiares. Por isso a tela só pode dizer isso quando a consulta **respondeu**: falha de carga
 * mostra faixa de erro, e detector que quebrou aparece nomeado. Erro de API nunca vira afirmação
 * de negócio (regra da at-60), e nesta tela essa confusão custaria caro — "está tudo bem" é
 * exatamente a leitura errada mais provável.
 *
 * ## O número é do detector; o texto é da IA
 *
 * O valor em destaque de cada card vem de uma consulta determinística e é conferível nas telas do
 * Contac. A IA só redige o parágrafo. Quando ela não está configurada, o card sai com o texto
 * automático e diz isso — o Radar não depende da IA para funcionar.
 */

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta tela mostra',
    conteudo: (
      <>
        <p>
          O Radar observa os dados do Contac e mostra <strong>apenas o que merece atenção</strong>.
          Cada cartão é um comportamento que cruzou um limite definido pelo sistema: placas no
          vermelho de forma recorrente, categorias negativas persistentes, associados acumulando
          boletos vencidos e contratos chegando ao fim.
        </p>
        <p>
          Se nenhum cartão aparecer, significa que <strong>nada cruzou os limites</strong> no
          período analisado. Quando alguma verificação falha, a tela avisa — a ausência de cartão
          nunca esconde um erro.
        </p>
      </>
    ),
  },
  {
    titulo: 'Prejuízo de evento não é o mesmo que prejuízo estrutural',
    conteudo: (
      <>
        <p>
          Quando um veículo sofre um evento (colisão, roubo, furto), a associação paga peças e
          reparo, e aquela placa fica muito negativa no período. Isso é a{' '}
          <strong>proteção veicular funcionando</strong>: todos contribuem, e quem tem evento
          consome. Não é um problema a resolver.
        </p>
        <p>
          Por isso o Radar separa as duas coisas. O cartão{' '}
          <strong>Placas no vermelho de forma recorrente</strong> mostra apenas o prejuízo{' '}
          <strong>estrutural</strong> — aquele que nenhum evento explica, em que a mensalidade não
          cobre o custo da placa. O prejuízo causado por evento aparece à parte, em{' '}
          <strong>Prejuízo explicado por evento</strong>, como informação e não como alerta.
        </p>
        <p>
          O que realmente chama atenção num evento é a repetição ou o papel do associado: por isso
          existem os cartões <strong>Placas com eventos recorrentes como causador</strong> e{' '}
          <strong>Prejuízo com associado como vítima</strong> — neste último, o custo pode ser
          passível de ressarcimento junto ao responsável.
        </p>
        <p>
          A separação usa a base de eventos vinda do SGA. Enquanto ela não estiver disponível, o
          cartão de prejuízo recorrente <strong>avisa no botão ?</strong> que o cruzamento não pôde
          ser feito — e volta a somar as duas naturezas de prejuízo.
        </p>
      </>
    ),
  },
  {
    titulo: 'Por que "Sem categoria definida" não aparece nos cartões',
    conteudo: (
      <>
        <p>
          <strong>Sem categoria definida não é uma categoria</strong>: é o conjunto de placas cuja
          origem não foi possível atribuir. Esse bloco é negativo{' '}
          <strong>por construção</strong>, e não por economia ruim — a receita da associação é
          principalmente mensalidade, que já chega com categoria, enquanto boa parte das despesas
          chega de outra origem, onde cabem veículos de terceiros e placas em formato antigo.
        </p>
        <p>
          Se ele entrasse no cartão de categorias negativas, apareceria sempre no topo e empurraria
          para baixo as categorias que realmente merecem análise. Por isso fica de fora.
        </p>
        <p>
          O tamanho desse bloco continua sendo informação útil — mas de{' '}
          <strong>qualidade de cadastro</strong>: parte dele é esperada (veículo de terceiro não é
          associado, logo não tem categoria) e parte é problema a corrigir (placa digitada errada,
          ou placa que passou a ser Mercosul e ficou registrada nos dois formatos).
        </p>
      </>
    ),
  },
  {
    titulo: 'De onde vem cada número',
    conteudo: (
      <>
        <p>
          Todo cartão tem um botão <strong>?</strong> que abre a origem daquele número: quais
          tabelas foram consultadas, qual é a regra, qual é o limite usado e o que aquele número{' '}
          <strong>não</strong> diz. Use isso antes de agir sobre uma recomendação.
        </p>
        <p>
          Os números são calculados pelo sistema e são os mesmos que as telas do Contac mostram
          para o mesmo período. Você pode conferir qualquer um deles no Dashboard de Placas ou de
          Categorias.
        </p>
      </>
    ),
  },
  {
    titulo: 'O papel da inteligência artificial',
    conteudo: (
      <>
        <p>
          O texto de cada recomendação é redigido por inteligência artificial, mas{' '}
          <strong>ela não calcula nada</strong>: recebe o número já pronto e escreve a explicação.
          Isso é deliberado — número gerado por IA não seria conferível.
        </p>
        <p>
          Na seção <strong>Achados do assistente</strong>, a inteligência artificial também{' '}
          <strong>explora</strong> os dados — mas só por meio de consultas pré-aprovadas do
          sistema, e o número de cada cartão continua sendo lido pelo próprio sistema do resultado
          da consulta, nunca do texto dela.
        </p>
        <p>
          Quando o recurso não está disponível no ambiente, o cartão exibe um texto automático e
          informa isso no rodapé. Os números continuam os mesmos.
        </p>
      </>
    ),
  },
  {
    titulo: 'Achados candidatos do assistente',
    conteudo: (
      <>
        <p>
          Abaixo dos cartões fixos, a seção <strong>Achados do assistente</strong> traz padrões que
          a inteligência artificial encontrou combinando dimensões (categoria, regional, base,
          consultor) que nenhuma verificação fixa cobre. Cada um é um{' '}
          <strong>candidato a atenção</strong> — por isso o selo e o visual diferente: é uma
          hipótese a conferir, não um número já validado por uma regra fixa do sistema.
        </p>
        <p>
          O que o candidato tem de garantido: o número do cartão vem de uma{' '}
          <strong>consulta pré-aprovada do sistema</strong> (o botão ? mostra a regra e as
          limitações), e proposta da inteligência artificial que não bate com a consulta é
          descartada — a seção diz quantas foram. O que ele não tem: um limite fixo decidido por
          alguém; a relevância é julgamento do assistente, e a palavra final é sua.
        </p>
        <p>
          A seção carrega separada dos cartões fixos: se ela falhar, os cartões continuam valendo —
          e a falha aparece escrita, nunca como "sem candidatos".
        </p>
        <p>
          Os achados do assistente são <strong>um conjunto por dia</strong>, analisado sobre um
          período fixo que a própria seção declara. Todo mundo que abre este Radar vê e avalia{' '}
          <strong>os mesmos achados</strong> até o dia seguinte — é isso que torna a avaliação da
          equipe comparável.
        </p>
      </>
    ),
  },
  {
    titulo: 'Sua avaliação',
    conteudo: (
      <>
        <p>
          Cada achado do assistente tem <strong>Útil</strong> e <strong>Não útil</strong>. O voto
          não é opinião registrada e esquecida: ele muda o que o Radar procura amanhã. Um recorte
          que a equipe reprova <strong>deixa de ser proposto</strong> — e é barrado mesmo que a
          inteligência artificial insista nele. Um recorte aprovado{' '}
          <strong>passa a ser acompanhado todo dia</strong>, com o número atualizado sem nova busca.
        </p>
        <p>
          Depois de votar, você pode dizer o motivo e escrever um comentário — os dois são
          opcionais. O que você escreve é levado ao assistente na análise seguinte{' '}
          <strong>como opinião sobre a utilidade do achado</strong>, nunca como ordem, e fica
          gravado com o seu nome.
        </p>
        <p>
          <strong>A contagem de votos não aparece aqui</strong>, de propósito: ver "12 pessoas
          acharam útil" antes de votar influencia o próprio voto. Quem precisa acompanhar os
          números vê tudo em Administração do Sistema, no módulo Radar.
        </p>
      </>
    ),
  },
  {
    titulo: 'Recomendação é hipótese, não decisão',
    conteudo: (
      <p>
        O Radar aponta onde olhar; ele <strong>não sabe a causa</strong> do que encontrou. Uma placa
        no vermelho pode ter um evento pontual caro, e um associado pode ter parado de pagar por
        motivo já resolvido. Trate cada cartão como ponto de partida para verificação, nunca como
        conclusão.
      </p>
    ),
  },
  {
    titulo: 'Por que o período não é escolhido aqui',
    conteudo: (
      <p>
        O Radar analisa sempre as últimas competências fechadas disponíveis, e mostra quais são no
        alto da tela. Os dados do Contac têm cerca de <strong>dois meses de defasagem</strong> — o
        mês corrente ainda não existe na base, e por isso não pode ser analisado.
      </p>
    ),
  },
];

const RadarFinanceiroPage: React.FC = () => {
  const [cards, setCards] = useState<CardDoRadar[]>([]);
  const [competenciaBase, setCompetenciaBase] = useState('');
  const [competenciaInicio, setCompetenciaInicio] = useState('');
  const [detectoresComFalha, setDetectoresComFalha] = useState<string[]>([]);
  /**
   * at-83: quem desativou este Radar e quando. `null` = ligado.
   *
   * 🔴 Precisa de estado próprio, e não de um `cards.length === 0`: "desativado" e "nada merece
   * atenção" são as duas leituras mais opostas possíveis nesta tela — a segunda tranquiliza a
   * diretoria sobre um Radar que não está olhando nada.
   */
  const [desativacao, setDesativacao] = useState<{ por: string | null; em: string | null } | null>(
    null,
  );
  const [iaConfigurada, setIaConfigurada] = useState(true);
  const [carregando, setCarregando] = useState(true);
  const [erroDeCarga, setErroDeCarga] = useState(false);

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [cardExplicado, setCardExplicado] = useState<CardDoRadar | null>(null);
  const [detalheAberto, setDetalheAberto] = useState<CardDoRadar | null>(null);
  /**
   * O achado cujas linhas estão abertas. O estado fica AQUI, e não na seção compartilhada: a ação
   * do cartão é do radar (aqui "Ver linhas", no Relacionamento "Ver associados"), e a seção só
   * pergunta ao dono do radar o que desenhar.
   */
  const [linhasAbertas, setLinhasAbertas] = useState<AchadoDoAssistente | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const dados = await buscarRecomendacoes();
      setCards(dados.cards);
      setCompetenciaBase(dados.competenciaBase);
      setCompetenciaInicio(dados.competenciaInicio);
      setDetectoresComFalha(dados.detectoresComFalha);
      setIaConfigurada(dados.iaConfigurada);
      setDesativacao(dados.desativacao ?? null);
      setErroDeCarga(false);
    } catch (error) {
      // Estado seguro E sinal. Sem os dois, a tela vazia diria "nada merece atenção" — que numa
      // tela de diretoria é uma afirmação de negócio, não um estado de carregamento.
      setCards([]);
      setErroDeCarga(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar as recomendações. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const periodoEscrito = useMemo(
    () => (competenciaInicio && competenciaBase ? `${competenciaInicio} a ${competenciaBase}` : ''),
    [competenciaInicio, competenciaBase],
  );

  const houveFalhaParcial = detectoresComFalha.length > 0;

  return (
    <div className="ConteDo recepcao-conte-do do-pagina">
      <div className="recepcao-header-row">
        <div
          className="recepcao-title"
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          Radar Financeiro
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      {periodoEscrito && (
        <p className="do-nota-janela">
          Competências analisadas: <strong>{periodoEscrito}</strong>. Os dados do Contac têm cerca
          de dois meses de defasagem, então o mês corrente ainda não entra na análise.
        </p>
      )}

      {erroDeCarga && (
        <p className="erro-de-tela">
          Não foi possível carregar as recomendações. Esta tela não está afirmando que está tudo
          bem — ela não conseguiu verificar. Recarregue a página; se persistir, acione a TI.
        </p>
      )}

      {houveFalhaParcial && !erroDeCarga && (
        <p className="erro-de-tela">
          Algumas verificações falharam e não estão representadas abaixo:{' '}
          {detectoresComFalha.map((id) => rotuloDoDetector(id)).join(', ')}. O que aparece nesta
          tela continua válido.
        </p>
      )}

      {carregando && (
        <div className="radar-cards" role="status" aria-label="Analisando os dados do Contac">
          <EsqueletoDeCard />
          <EsqueletoDeCard />
          <EsqueletoDeCard />
        </div>
      )}

      {/*
        🔴 at-83 — o bloco de DESATIVADO vem ANTES do vazio e o exclui. Sem isto, um Radar
        desligado exibiria "Nenhuma recomendação no momento. As verificações responderam
        normalmente", que é uma afirmação falsa sobre verificações que não aconteceram — e a mais
        cara desta tela, porque tranquiliza a diretoria.
      */}
      {!carregando && !erroDeCarga && desativacao && (
        <div className="radar-vazio">
          <p className="radar-vazio-titulo">O Radar está desativado pela Administração do Sistema.</p>
          <p className="radar-vazio-apoio">
            {desativacao.por
              ? `Desativado por ${desativacao.por}${
                  desativacao.em
                    ? ` em ${new Date(desativacao.em).toLocaleDateString('pt-BR')}`
                    : ''
                }. `
              : ''}
            Enquanto estiver assim, nenhuma verificação é executada — esta tela não está afirmando
            que não há nada a observar.
          </p>
        </div>
      )}

      {!carregando && !erroDeCarga && !desativacao && cards.length === 0 && !houveFalhaParcial && (
        <div className="radar-vazio">
          <p className="radar-vazio-titulo">Nenhuma recomendação no momento.</p>
          <p className="radar-vazio-apoio">
            Nenhum comportamento cruzou os limites verificados nas competências de {periodoEscrito}.
            As verificações responderam normalmente.
          </p>
        </div>
      )}

      {!carregando && cards.length > 0 && (
        <div className="radar-cards">
          {/*
            Número, unidade e valor na MESMA linha (visual da at-12): como linhas separadas, o
            valor monetário lia como legenda do número, e não como a segunda informação do cartão.
            Quem cuida disso agora é o `RadarCard` compartilhado (at-82).
          */}
          {cards.map((card) => (
            <RadarCard
              key={card.detectorId}
              titulo={card.titulo}
              numero={card.numero}
              formato="numero"
              unidade={card.unidade}
              valor={card.valor}
              texto={card.recomendacao}
              onExplicar={() => setCardExplicado(card)}
              acoes={
                <button
                  type="button"
                  className="do-btn-filtros"
                  onClick={() => setDetalheAberto(card)}
                >
                  Ver {card.unidade}
                </button>
              }
              rodape={
                card.origemRedacao === 'template' ? (
                  <p className="radar-origem">
                    Texto automático — o assistente de inteligência artificial não está configurado
                    neste ambiente. O número acima não é afetado.
                  </p>
                ) : undefined
              }
            />
          ))}
        </div>
      )}

      {!iaConfigurada && !carregando && cards.length > 0 && (
        <p className="aviso-de-tela">
          As recomendações estão sendo exibidas com o texto automático do sistema. Para ativar a
          redação por inteligência artificial, acione a TI.
        </p>
      )}

      {/*
        A seção de achados do assistente é o COMPONENTE compartilhado (at-82): a mesma peça
        desenha esta tela e o painel do Relacionamento. Ela carrega separada dos cards
        determinísticos, de propósito — a exploração por IA é mais lenta e pode falhar sozinha, e
        a falha dela nunca pode segurar nem contaminar os números dos detectores.
      */}
      <SecaoAchadosDoAssistente
        radarId="financeiro"
        apoio={
          <>
            Padrões que a inteligência artificial encontrou explorando consultas pré-aprovadas do
            sistema. Cada um é um <strong>candidato</strong> a atenção — confira o número pelo botão
            ? antes de agir.
          </>
        }
        comoConferir="Abra a lista pelo botão do cartão e compare com o Dashboard de Placas ou de Categorias no mesmo período. Os números vêm das mesmas fontes."
        renderAcoes={(achado) => (
          <button type="button" className="do-btn-filtros" onClick={() => setLinhasAbertas(achado)}>
            Ver linhas
          </button>
        )}
      />


      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Radar Financeiro"
        secoes={helpSections}
      />

      {cardExplicado && (
        <ComoFunciona
          isOpen
          onClose={() => setCardExplicado(null)}
          titulo={`De onde vem: ${cardExplicado.titulo}`}
          secoes={secoesDaProveniencia(
            cardExplicado.proveniencia,
            'Abra a lista pelo botão do cartão e compare com o Dashboard de Placas ou de Categorias no mesmo período. Os números vêm das mesmas fontes.',
          )}
        />
      )}

      {detalheAberto && (
        <DetalheRecomendacaoModal
          detectorId={detalheAberto.detectorId}
          titulo={detalheAberto.titulo}
          onClose={() => setDetalheAberto(null)}
        />
      )}

      {linhasAbertas && (
        <LinhasDoAchadoModal achado={linhasAbertas} onClose={() => setLinhasAbertas(null)} />
      )}
    </div>
  );
};

const ROTULOS: Record<string, string> = {
  'placas-no-vermelho': 'placas no vermelho',
  'categorias-negativas': 'categorias negativas',
  'inadimplencia-crescente': 'inadimplência',
  'contratos-perto-do-fim': 'contratos perto do fim',
  'prejuizo-por-evento': 'prejuízo explicado por evento',
  'reincidencia-de-eventos': 'eventos recorrentes como causador',
  'vitima-com-prejuizo': 'prejuízo com associado como vítima',
};

function rotuloDoDetector(id: string): string {
  return ROTULOS[id] ?? id;
}

export default RadarFinanceiroPage;
